import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSessionApi, getGuestStore } from "../../../utils/demoApi";
import { resolveSessionKind } from "../sessionKind";
import DemoPublicShell from "./DemoPublicShell";
import useAttendeeToken from "./useAttendeeToken";
import "../demo-classes.scss";

/**
 * DemoJoinPage — the one URL in every message we send an attendee.
 *
 *   /{demo|webinar}/{slug}/join?t=<token>
 *
 * It absorbs the credential, then routes by what is actually true right now:
 * the lobby before the session, the room during it, the feedback form after.
 * That is the whole point of having a single link — a reminder opened three
 * days late lands on the feedback form instead of a dead lobby, so nothing we
 * post into somebody's inbox can go stale.
 *
 * ⚠️ It renders no session detail of its own and holds no state worth seeing.
 * It is a decision, and it should be over before anyone reads it.
 *
 * The four outcomes:
 *   - no token at all      → the public landing page, which offers the form
 *   - token rejected (401) → clear it and send them to the form, honestly
 *   - session gone (404)   → the landing page, which renders its own message
 *   - otherwise            → lobby / live / thank-you
 */
const DemoJoinPage = ({ kind = "demo" }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const vocab = resolveSessionKind(kind);
  const api = getSessionApi(vocab.kind);
  const token = useAttendeeToken(vocab.kind, slug);

  const [error, setError] = useState("");

  useEffect(() => {
    const landing = `${vocab.publicBase}/${slug}`;

    // Nothing to resume — they followed a bare /join, or opened the link in a
    // browser that stripped the query. The landing page is the right answer:
    // it shows the session and offers registration.
    if (!token) {
      navigate(landing, { replace: true });
      return undefined;
    }

    let cancelled = false;
    api.getMe(slug, token)
      .then(({ data }) => {
        if (cancelled) return;
        // `sessionStatus` comes back on the same call, so the routing decision
        // costs one request rather than two.
        if (data.sessionStatus === "live") {
          navigate(`${vocab.publicBase}/${slug}/live`, { replace: true });
        } else if (data.sessionStatus === "ended" || data.sessionStatus === "cancelled") {
          navigate(`${vocab.publicBase}/${slug}/thank-you`, { replace: true });
        } else {
          navigate(`${vocab.publicBase}/${slug}/lobby`, { replace: true });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Expired or revoked. Drop the dead credential so the next page does
          // not try it again, and put them in front of the form.
          getGuestStore(vocab.kind).clear(slug);
          navigate(`${landing}/register`, { replace: true });
          return;
        }
        if (status === 404 || status === 410) {
          navigate(landing, { replace: true });
          return;
        }
        setError("We couldn't open your link just now. Please try again in a moment.");
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, kind, navigate]);

  return (
    <DemoPublicShell kind={vocab.kind}>
      {error
        ? <p className="demo-public__error">{error}</p>
        : <p className="demo-public__pulse">Opening your {vocab.noun}…</p>}
    </DemoPublicShell>
  );
};

export default DemoJoinPage;
